import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import ArticleCard from "@/components/feature/ArticleCard";
import { getArticlesByCategory, getCategory, formatDate } from "@/utils/content";

const PAGE_SIZE = 5;
const sortOptions = [
  { value: "new", label: "时间由新到旧" },
  { value: "hot", label: "按热度排序" },
];
const timeOptions = [
  { value: "all", label: "全部时间" },
  { value: "week", label: "最近一周" },
  { value: "month", label: "最近一月" },
];

export default function CategoryPage() {
  const { type = "" } = useParams();
  const category = getCategory(type);
  const baseList = useMemo(() => getArticlesByCategory(type), [type]);

  const [activeTag, setActiveTag] = useState("all");
  const [sort, setSort] = useState("new");
  const [time, setTime] = useState("all");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    baseList.forEach((a) => a.tags.forEach((t) => tags.add(t)));
    return ["all", ...Array.from(tags)];
  }, [baseList]);

  const filtered = useMemo(() => {
    let list = [...baseList];
    if (activeTag !== "all") list = list.filter((a) => a.tags.includes(activeTag));

    if (time !== "all") {
      const newest = baseList.reduce((acc, a) => (a.date > acc ? a.date : acc), "0000-00-00");
      const base = new Date(newest).getTime();
      const span = time === "week" ? 7 : 30;
      list = list.filter((a) => (base - new Date(a.date).getTime()) / 86400000 <= span);
    }

    if (sort === "hot") list.sort((a, b) => b.views - a.views);
    else list.sort((a, b) => (a.date < b.date ? 1 : -1));

    return list;
  }, [baseList, activeTag, sort, time]);

  const resetAll = () => {
    setActiveTag("all");
    setSort("new");
    setTime("all");
    setVisible(PAGE_SIZE);
  };

  if (!category) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-6">
        <Seo title="分类不存在 - 噜噜社" description="未找到对应的内容分类。" />
        <i className="ri-error-warning-line text-5xl text-foreground-300" />
        <h1 className="mt-4 text-2xl font-bold text-foreground-950">未找到该分类</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的分类不存在，返回首页看看其他内容吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const shown = filtered.slice(0, visible);

  return (
    <>
      <Seo
        title={`噜噜社 - ${category.name}资讯分类`}
        description={`噜噜社${category.name}频道：${category.description}`}
        keywords={`噜噜社,${category.name}资讯,${category.enName},潮流文化`}
      />

      {/* 分类头部 */}
      <section className="relative h-56 w-full overflow-hidden md:h-72">
        <img src={category.cover} alt={category.name} title={`${category.name}资讯 - 噜噜社`} className="h-full w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/85 via-secondary-950/60 to-secondary-950/30" />
        <div className="absolute inset-0 mx-auto flex w-full max-w-[1280px] flex-col justify-center px-4 md:px-6">
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-primary-300">{category.enName}</span>
          <h1 className="mt-2 text-3xl font-extrabold text-background-50 md:text-4xl">{category.name}资讯</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-100/90 line-clamp-2 md:text-base">
            {category.description}
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6">
        {/* 面包屑 */}
        <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span>分类</span>
          <i className="ri-arrow-right-s-line" />
          <span className="font-medium text-foreground-900">{category.name}</span>
        </nav>

        {/* 筛选栏 */}
        <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
              <i className="ri-filter-3-line text-primary-500" />
              筛选与排序
            </div>
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 md:hidden"
            >
              {filterOpen ? "收起" : "展开"}
              <i className={`ri-arrow-down-s-line transition-transform ${filterOpen ? "rotate-180" : ""}`} />
            </button>
          </div>

          <div className={`${filterOpen ? "block" : "hidden"} md:block`}>
            <div className="mt-4">
              <p className="mb-2 text-xs font-medium text-foreground-400">标签筛选</p>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      setActiveTag(tag);
                      setVisible(PAGE_SIZE);
                    }}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      activeTag === tag
                        ? "bg-primary-500 text-background-50"
                        : "bg-secondary-100 text-secondary-900 hover:bg-primary-100 hover:text-primary-800"
                    }`}
                  >
                    {tag === "all" ? "全部标签" : tag}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <span className="text-xs font-medium text-foreground-400">发布时间</span>
                <div className="flex flex-wrap gap-1 rounded-full bg-background-200/70 p-1">
                  {timeOptions.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => {
                        setTime(t.value);
                        setVisible(PAGE_SIZE);
                      }}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                        time === t.value ? "bg-accent-500 text-background-50" : "text-foreground-600"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <span className="text-xs font-medium text-foreground-400">排序</span>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-800 outline-none focus:border-primary-400"
                >
                  {sortOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={resetAll}
                className="flex w-fit cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          </div>
        </div>

        {/* 结果统计 */}
        <div className="mt-6 flex items-center justify-between text-sm text-foreground-500">
          <span>
            共找到 <strong className="text-primary-600">{filtered.length}</strong> 篇{category.name}内容
          </span>
          <span className="hidden sm:block">更新于 {baseList.length > 0 ? formatDate(baseList[0].date) : "—"}</span>
        </div>

        {/* 列表 */}
        {shown.length > 0 ? (
          <div className="mt-4 flex flex-col gap-4">
            {shown.map((a) => (
              <ArticleCard key={a.id} article={a} variant="list" showCategory={false} />
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-16 text-center">
            <i className="ri-search-eye-line text-5xl text-foreground-300" />
            <h3 className="mt-4 text-lg font-semibold text-foreground-900">没有符合条件的内容</h3>
            <p className="mt-2 text-sm text-foreground-500">试试减少筛选条件，或重置全部筛选。</p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}

        {/* 加载更多 */}
        {visible < filtered.length && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              加载更多（剩余 {filtered.length - visible} 篇）
            </button>
          </div>
        )}
      </div>
    </>
  );
}